/**
 * Alpine.js initialization and configuration
 */
import Alpine from 'alpinejs';
import collapse from '@alpinejs/collapse';
import intersect from '@alpinejs/intersect';

// Import the language components
import { availableLanguages, popularLanguages, languageEditor } from './alpine-language-editor';
import { languageModal } from './alpine-language-modal';

// Import profile editing components
import { textEditor } from './alpine-text-editor';
import { inputEditor } from './alpine-input-editor';
import { nameUsernameEditor } from './alpine-name-username-editor';
import { selectEditor } from './alpine-select-editor';
import { multiselectEditor } from './alpine-multiselect-editor';
import { statusEditor } from './alpine-status-editor';
import { statusPostComposer } from './alpine-status-post-composer';
import { cityEditor } from './alpine-city-editor';
// Import profile language modal
import { profileLanguageModal } from './profile-language-modal.js';

// Import shared profile photo uploader
import { profilePhotoUploader } from './profile-photo-uploader.js';

// Import langex component
import { langexComponent } from './langex.js';

// Import notifications panel component
import { notificationsPanel } from '../interpals/notifications.js';

// Import quick search filters component
import { quickSearchFilters } from './alpine-quick-search.js';

// Import user carousel component
import { userCarousel } from './alpine-user-carousel.js';

// Import speakers language switcher component
import { speakersLanguageSwitcher } from './alpine-speakers-switcher.js';

// Import loading button component
import { loadingButton } from './alpine-loading-button.js';

// Import compact profile scroll hint component
import { compactProfileScrollHint } from './alpine-compact-profile-scroll-hint.js';

// Import bookmark button component
import { bookmarkButton } from './alpine-bookmark-button.js';

// Import passkey settings component
import { passkeySettings } from './alpine-passkey-settings.js';

// Import photo moderation modal
import { photoModModal } from '../interpals/photo-mod-modal.js';

// Import moderator appeal decision component
import { modAppealDecide } from '../interpals/mod-appeals.js';

// Import moderator deleted-accounts queue components
import {
    modDeletedAccountsPageModal,
    modDeletedAccountRow,
} from '../interpals/mod-deleted-accounts.js';

// Import suspension-appeal form component
import { appealForm } from './alpine-appeal-form.js';

// Import shared image-file attacher component
import { fileAttacher } from './alpine-file-attacher.js';

// Import trip editor component
import { tripEditor } from './alpine-trip-editor.js';

// Import interests modal components
import { interestsModal, interestsDisplay, searchInterestsFilter, interestsBrowseSheet } from '../interpals/interests-modal.js';

// Import responsive filter chip component
import { responsiveFilterChip } from './alpine-responsive-filter.js';

// Import search filters modal component
import { searchFiltersModal } from './alpine-search-filters-modal.js';

// Import nearby chip component
import { nearbyChip } from './alpine-nearby-chip.js';

// Import country picker components
import { countryChips, countryPickerModal } from './alpine-country-picker.js';

// Import language picker components
import { languageChips, languagePickerModal } from './alpine-language-picker.js';

// Import city filter component for search location
import { cityFilter } from './alpine-city-filter.js';

// Import language overflow component
import { languageOverflow } from './alpine-language-overflow.js';

// Import like button and likers modal components
import { likeButton } from './alpine-like-button.js';
import { likersModal } from './alpine-likers-modal.js';
import { inlineComments } from './alpine-inline-comments.js';

// Import report modal component
import { reportModal } from './report-modal.js';

// Import block & delete modal component
import { blockDeleteModal } from './block-delete-modal.js';

// Import photo viewer components
import { mobilePhotoViewer } from './alpine-mobile-photo-viewer.js';
import { desktopPhotoViewer } from './alpine-desktop-photo-viewer.js';

// Import language selector component for settings page
import { languageSelector } from './alpine-language-selector.js';

// Import footer language picker component
import { footerLanguagePicker } from './alpine-footer-language-picker.js';

// Import ultra-legacy yellow member-nav collapse component
import { yellowBarCollapse } from './alpine-yellow-bar.js';

// Import the profile store
import './alpine-profile-store';

// Clear saved signup wizard state when not on the signup page to avoid stale data
try {
    const path = window.location.pathname || '';
    if (!path.includes('/signup')) {
        localStorage.removeItem('signupWizardState');
    }
} catch (e) {
    // Ignore storage access issues
}

// Configure Alpine.js
Alpine.prefix('x-');

// Register plugins
Alpine.plugin(collapse);
Alpine.plugin(intersect);

// Set up Alpine store for i18n state (translations loaded asynchronously)
// Components can use $store.i18n.ready to trigger re-renders when translations load
// Check if translations were already loaded before Alpine initialized (cached translations)
Alpine.store('i18n', {
    ready: window.I18n?.keys().length > 0
});

// Set up Alpine store for shared language data
Alpine.store('languageData', {
    available: availableLanguages,
    popular: popularLanguages,
    
    // Centralized filtering method
    getFiltered(existingCodes, searchTerm = '') {
        const term = searchTerm.toLowerCase();
        return Object.entries(this.available)
            .filter(([code, name]) => 
                !existingCodes.has(code) && 
                (name.toLowerCase().includes(term) || code.toLowerCase().includes(term))
            )
            .sort(([, nameA], [, nameB]) => nameA.localeCompare(nameB));
    }
});

// Make Alpine globally available before starting
window.Alpine = Alpine;

// Register Alpine data components
Alpine.data('languageEditor', languageEditor);
Alpine.data('languageModal', languageModal);
Alpine.data('textEditor', textEditor);
Alpine.data('inputEditor', inputEditor);
Alpine.data('nameUsernameEditor', nameUsernameEditor);
Alpine.data('selectEditor', selectEditor);
Alpine.data('multiselectEditor', multiselectEditor);
Alpine.data('statusEditor', statusEditor);
Alpine.data('statusPostComposer', statusPostComposer);
Alpine.data('cityEditor', cityEditor);
// Register profile language modal
Alpine.data('profileLanguageModal', profileLanguageModal);

// Register shared photo uploader component (used by profile page)
Alpine.data('profilePhotoUploader', profilePhotoUploader);

// Register langex component
Alpine.data('langexComponent', langexComponent);

// Register notifications panel component
Alpine.data('notificationsPanel', notificationsPanel);


// Register quick search filters component
Alpine.data('quickSearchFilters', quickSearchFilters);

// Register user carousel component
Alpine.data('userCarousel', userCarousel);

// Register speakers language switcher component
Alpine.data('speakersLanguageSwitcher', speakersLanguageSwitcher);

// Register loading button component
Alpine.data('loadingButton', loadingButton);

// Register compact profile scroll hint component
Alpine.data('compactProfileScrollHint', compactProfileScrollHint);

// Register bookmark button component
Alpine.data('bookmarkButton', bookmarkButton);

// Register passkey settings component
Alpine.data('passkeySettings', passkeySettings);

// Register photo moderation modal
Alpine.data('photoModModal', photoModModal);

// Register moderator appeal decision component
Alpine.data('modAppealDecide', modAppealDecide);

// Register moderator deleted-accounts queue components
Alpine.data('modDeletedAccountsPageModal', modDeletedAccountsPageModal);
Alpine.data('modDeletedAccountRow', modDeletedAccountRow);

// Register suspension-appeal form component
Alpine.data('appealForm', appealForm);

// Register shared image-file attacher component
Alpine.data('fileAttacher', fileAttacher);

// Register trip editor component
Alpine.data('tripEditor', tripEditor);

// Register interests modal components
Alpine.data('interestsModal', interestsModal);
Alpine.data('interestsDisplay', interestsDisplay);
Alpine.data('searchInterestsFilter', searchInterestsFilter);
Alpine.data('interestsBrowseSheet', interestsBrowseSheet);

// Register responsive filter chip component
Alpine.data('responsiveFilterChip', responsiveFilterChip);

// Register search filters modal component
Alpine.data('searchFiltersModal', searchFiltersModal);

// Register nearby chip component
Alpine.data('nearbyChip', nearbyChip);

// Register country picker components
Alpine.data('countryChips', countryChips);
Alpine.data('countryPickerModal', countryPickerModal);

// Register language picker components
Alpine.data('languageChips', languageChips);
Alpine.data('languagePickerModal', languagePickerModal);

// Register city filter component
Alpine.data('cityFilter', cityFilter);

// Register language overflow component
Alpine.data('languageOverflow', languageOverflow);

// Register like button and likers modal components
Alpine.data('likeButton', likeButton);
Alpine.data('likersModal', likersModal);
Alpine.data('inlineComments', inlineComments);

// Register report modal component
Alpine.data('reportModal', reportModal);

// Register block & delete modal component
Alpine.data('blockDeleteModal', blockDeleteModal);

// Register language selector component for settings page
Alpine.data('languageSelector', languageSelector);

// Register footer language picker component
Alpine.data('footerLanguagePicker', footerLanguagePicker);

// Register ultra-legacy yellow member-nav collapse component
Alpine.data('yellowBarCollapse', yellowBarCollapse);

// Register photo viewer components
Alpine.data('mobilePhotoViewer', mobilePhotoViewer);
Alpine.data('desktopPhotoViewer', desktopPhotoViewer);

// Lazy-load page-specific components based on current route
const path = window.location.pathname;
const lazyComponents = [];

if (path.includes('/signup')) {
    lazyComponents.push(
        import(/* webpackPrefetch: true */ './signup/index.js').then(m => Alpine.data('signupWizard', m.signupWizard))
    );
}

if (path.includes('/getting-started')) {
    lazyComponents.push(
        import(/* webpackPrefetch: true */ './getting-started/index.js').then(m => {
            Alpine.data('gettingStartedGoals', m.gettingStartedGoals);
            Alpine.data('gettingStartedLanguages', m.gettingStartedLanguages);
            Alpine.data('gettingStartedAbout', m.gettingStartedAbout);
            Alpine.data('gettingStartedPhoto', m.gettingStartedPhoto);
        })
    );
}

if (path.includes('/album') || path.includes('/photos')) {
    lazyComponents.push(
        import(/* webpackPrefetch: true */ './album-editor.js').then(m => Alpine.data('albumEditor', m.albumEditor)),
        import(/* webpackPrefetch: true */ './alpine-album-details-inline-editor.js').then(m => Alpine.data('albumDetailsInlineEditor', m.albumDetailsInlineEditor))
    );
}

if (path.includes('/global-feed') || path.includes('/account')) {
    lazyComponents.push(
        import(/* webpackPrefetch: true */ '../interpals/global-feed.js').then(m => Alpine.data('globalFeedComponent', m.globalFeedComponent))
    );
}

if (path.includes('/mod')) {
    lazyComponents.push(
        import(/* webpackPrefetch: true */ './moderation/moderator-privileges.js').then(m => Alpine.data('moderatorPrivileges', m.moderatorPrivileges)),
        import(/* webpackPrefetch: true */ './moderation/mod-activity-heatmap.js').then(m => Alpine.data('modActivityHeatmap', m.modActivityHeatmap))
    );
}

if (path.includes('/mod/settings/deletion-rule/')) {
    lazyComponents.push(
        import(/* webpackPrefetch: true */ './moderation/mod-text-filter-deletions.js')
            .then(m => Alpine.data('modTextFilterDeletions', m.modTextFilterDeletions))
    );
} else if (path.includes('/mod/settings')) {
    lazyComponents.push(
        import(/* webpackPrefetch: true */ './moderation/mod-settings.js').then(m => Alpine.data('modSettings', m.modSettings))
    );
}

// Wait for lazy components and the async i18n payload, then start Alpine so
// components never render raw `common.foo` keys before translations load.
// I18n.ready() returns the loadFromUrl() promise; on a localStorage cache hit
// (returning visitor on the same translations version) the cache branch runs
// synchronously and populates I18n.translations before the promise resolves,
// so the only added latency is a single microtask — no observable delay.
// Fetch failures resolve the promise without populating translations, so
// Alpine still starts; the failure mode (raw keys) matches existing behavior.
const translationsReady = window.I18n?.ready ? window.I18n.ready() : Promise.resolve();
Promise.all([translationsReady, ...lazyComponents]).then(() => Alpine.start());

// Export for use in other modules
export default Alpine;
